/**
 * HM-431 cluster 22 Report Statistiche: pagina /report con 5 sezioni
 * (occupazione, revenue, top clienti, ore extra, no-show) + selettore
 * periodo (preset + custom range) + export CSV.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reports } from '../../api/reports.js';

type Preset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(p: Preset): { from: Date; to: Date } {
  const now = new Date();
  if (p === 'today') return { from: startOfDay(now), to: endOfDay(now) };
  if (p === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return { from: startOfDay(y), to: endOfDay(y) };
  }
  if (p === 'week') {
    const w = new Date(now);
    w.setDate(w.getDate() - 7);
    return { from: startOfDay(w), to: endOfDay(now) };
  }
  // month default
  const m = new Date(now);
  m.setDate(m.getDate() - 30);
  return { from: startOfDay(m), to: endOfDay(now) };
}

function isoLocalDate(d: Date): string {
  // YYYY-MM-DD per <input type="date">
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PALETTE = {
  primary: '#E97B3D',
  secondary: '#3D2F24',
  tertiary: '#A89A88',
  bg: '#FAF7F2',
  text: '#3D2F24',
};

export function ReportPage() {
  const [preset, setPreset] = useState<Preset>('month');
  const initial = presetRange('month');
  const [customFrom, setCustomFrom] = useState<string>(isoLocalDate(initial.from));
  const [customTo, setCustomTo] = useState<string>(isoLocalDate(initial.to));

  const range = useMemo(() => {
    if (preset === 'custom') {
      return {
        from: new Date(`${customFrom}T00:00:00.000Z`).toISOString(),
        to: new Date(`${customTo}T23:59:59.999Z`).toISOString(),
      };
    }
    const r = presetRange(preset);
    return { from: r.from.toISOString(), to: r.to.toISOString() };
  }, [preset, customFrom, customTo]);

  const occQuery = useQuery({
    queryKey: ['reports', 'occupancy', range],
    queryFn: () => reports.occupancy(range),
  });
  const revQuery = useQuery({
    queryKey: ['reports', 'revenue', range],
    queryFn: () => reports.revenue(range),
  });
  const [sortBy, setSortBy] = useState<'spend' | 'frequency'>('spend');
  const topQuery = useQuery({
    queryKey: ['reports', 'guests-top', range, sortBy],
    queryFn: () => reports.guestsTop(range, sortBy),
  });
  const ehQuery = useQuery({
    queryKey: ['reports', 'extra-hours', range],
    queryFn: () => reports.extraHours(range),
  });
  const nsQuery = useQuery({
    queryKey: ['reports', 'no-show', range],
    queryFn: () => reports.noShow(range),
  });

  function downloadCsv(type: string) {
    const url = reports.exportCsv(type, range, type === 'guests-top' ? 50 : undefined);
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <div data-testid="report-page" style={{ padding: 24, color: PALETTE.text }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 16,
        }}
      >
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>Report</h1>
      </div>

      {/* SELETTORE PERIODO */}
      <div
        data-testid="report-period-selector"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 20,
          alignItems: 'center',
        }}
      >
        {(['today', 'yesterday', 'week', 'month', 'custom'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            data-testid={`report-preset-${p}`}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              border: '1px solid #D4C4B0',
              background: preset === p ? PALETTE.primary : '#fff',
              color: preset === p ? '#fff' : PALETTE.text,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {p === 'today'
              ? 'Oggi'
              : p === 'yesterday'
                ? 'Ieri'
                : p === 'week'
                  ? 'Ultima settimana'
                  : p === 'month'
                    ? 'Ultimo mese'
                    : 'Personalizzato'}
          </button>
        ))}
        {preset === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              data-testid="report-custom-from"
            />
            <span>→</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              data-testid="report-custom-to"
            />
          </span>
        )}
      </div>

      {/* OCCUPAZIONE */}
      <Section
        title="Occupazione"
        testid="report-section-occupancy"
        onExport={() => downloadCsv('occupancy')}
        isLoading={occQuery.isLoading}
        isEmpty={!occQuery.data}
      >
        {occQuery.data && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <Big value={`${occQuery.data.occupancyPct}%`} label="Occupazione media" />
              <div style={{ fontSize: 12, color: PALETTE.tertiary, marginTop: 4 }}>
                {occQuery.data.occupiedNights.toFixed(1)} /{' '}
                {occQuery.data.totalAvailableNights ?? '-'} notti · {occQuery.data.periodDays}{' '}
                giorni
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 320, height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={occQuery.data.byRoomType}>
                  <CartesianGrid stroke="#E8DECF" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} unit="%" />
                  <Tooltip />
                  <Bar dataKey="pct" fill={PALETTE.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Section>

      {/* REVENUE */}
      <Section
        title="Ricavi"
        testid="report-section-revenue"
        onExport={() => downloadCsv('revenue')}
        isLoading={revQuery.isLoading}
        isEmpty={!revQuery.data}
      >
        {revQuery.data && (
          <>
            {/* ONDATA 3 P5: prenotato ≠ incassato — due numeri, nomi onesti. */}
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
              <Big value={`€ ${revQuery.data.collectedRevenue.toFixed(2)}`} label="Incassato" />
              <Big value={`€ ${revQuery.data.bookedRevenue.toFixed(2)}`} label="Prenotato" />
              <Big
                value={`€ ${revQuery.data.byMode.dayuse.toFixed(2)}`}
                label="Day Use (prenotato)"
              />
              <Big
                value={`€ ${revQuery.data.byMode.overnight.toFixed(2)}`}
                label="Pernotto (prenotato)"
              />
              <Big
                value={`€ ${revQuery.data.avgPerBooking.toFixed(2)}`}
                label="Media/prenotazione"
              />
              <Big value={String(revQuery.data.bookingCount)} label="Prenotazioni" />
            </div>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revQuery.data.byDay}>
                  <CartesianGrid stroke="#E8DECF" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="collectedRevenue"
                    name="Incassato"
                    stroke={PALETTE.primary}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="bookedRevenue"
                    name="Prenotato"
                    stroke={PALETTE.primary}
                    strokeDasharray="5 4"
                    strokeOpacity={0.55}
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Section>

      {/* TOP CLIENTI */}
      <Section
        title="Top clienti"
        testid="report-section-top-guests"
        onExport={() => downloadCsv('guests-top')}
        isLoading={topQuery.isLoading}
        isEmpty={!topQuery.data || topQuery.data.items.length === 0}
        headerExtra={
          <div style={{ fontSize: 12 }}>
            Ordina per:{' '}
            <button
              type="button"
              data-testid="report-top-sort-spend"
              onClick={() => setSortBy('spend')}
              style={{
                padding: '2px 8px',
                marginRight: 4,
                border: '1px solid #D4C4B0',
                borderRadius: 4,
                background: sortBy === 'spend' ? PALETTE.primary : '#fff',
                color: sortBy === 'spend' ? '#fff' : PALETTE.text,
                cursor: 'pointer',
              }}
            >
              Spesa
            </button>
            <button
              type="button"
              data-testid="report-top-sort-frequency"
              onClick={() => setSortBy('frequency')}
              style={{
                padding: '2px 8px',
                border: '1px solid #D4C4B0',
                borderRadius: 4,
                background: sortBy === 'frequency' ? PALETTE.primary : '#fff',
                color: sortBy === 'frequency' ? '#fff' : PALETTE.text,
                cursor: 'pointer',
              }}
            >
              Frequenza
            </button>
          </div>
        }
      >
        {topQuery.data && (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: PALETTE.bg }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>#</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Cliente</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Speso</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Soggiorni</th>
              </tr>
            </thead>
            <tbody>
              {topQuery.data.items.map((g, idx) => (
                <tr key={g.guestId} style={{ borderTop: '1px solid #F0E8DA' }}>
                  <td style={{ padding: '6px 10px' }}>{idx + 1}</td>
                  <td style={{ padding: '6px 10px' }}>{g.guestName}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                    € {g.totalSpend.toFixed(2)}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{g.stayCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* ORE EXTRA */}
      <Section
        title="Ore extra"
        testid="report-section-extra-hours"
        onExport={() => downloadCsv('extra-hours')}
        isLoading={ehQuery.isLoading}
        isEmpty={!ehQuery.data || ehQuery.data.totalExtraHours === 0}
      >
        {ehQuery.data && (
          <>
            <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
              <Big value={String(ehQuery.data.totalExtraHours)} label="Ore extra totali" />
              <Big value={`€ ${ehQuery.data.extraRevenue.toFixed(2)}`} label="Ricavi ore extra" />
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ehQuery.data.byRoom}>
                  <CartesianGrid stroke="#E8DECF" strokeDasharray="3 3" />
                  <XAxis dataKey="room" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="hours" fill={PALETTE.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Section>

      {/* NO-SHOW */}
      <Section
        title="No-show"
        testid="report-section-no-show"
        onExport={() => downloadCsv('no-show')}
        isLoading={nsQuery.isLoading}
        isEmpty={!nsQuery.data}
      >
        {nsQuery.data && (
          <>
            <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
              <Big value={String(nsQuery.data.noShowCount)} label="No-show" />
              <Big value={`${nsQuery.data.noShowPct}%`} label="% sul totale" />
              <Big value={String(nsQuery.data.totalBookings)} label="Prenotazioni totali" />
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={nsQuery.data.byDay}>
                  <CartesianGrid stroke="#E8DECF" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#B85450"
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  testid,
  onExport,
  isLoading,
  isEmpty,
  headerExtra,
  children,
}: {
  title: string;
  testid: string;
  onExport: () => void;
  isLoading: boolean;
  isEmpty: boolean;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid={testid}
      style={{
        background: '#fff',
        border: '1px solid #E8DECF',
        borderRadius: 8,
        padding: 16,
        marginBottom: 18,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {headerExtra}
          <button
            type="button"
            onClick={onExport}
            data-testid={`${testid}-export`}
            style={{
              padding: '4px 10px',
              border: '1px solid #D4C4B0',
              borderRadius: 4,
              background: '#fff',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Esporta CSV
          </button>
        </div>
      </div>
      {isLoading && <div style={{ color: '#7A6957', fontSize: 13 }}>Caricamento…</div>}
      {!isLoading && isEmpty && (
        <div
          data-testid={`${testid}-empty`}
          style={{ color: '#7A6957', fontSize: 13, fontStyle: 'italic' }}
        >
          Nessun dato per il periodo selezionato.
        </div>
      )}
      {!isLoading && !isEmpty && children}
    </section>
  );
}

function Big({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 600, color: '#3D2F24' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#7A6957' }}>{label}</div>
    </div>
  );
}
